import {
  AddIcon,
  Alert,
  Button,
  CardResource,
  List,
  Skeleton,
  Stack,
  TaskItem,
  Typography,
} from '@all-manager/design-system-v2';
import AddPendingItemModal from "./AddPendingItemModal";
import PendingItemMenuActions from "./PendingItemMenuActions";
import { getPendingItemProviderLabel, usePendingItemsSection } from "./usePendingItemsSection";

type Props = {
  subTopicId: string;
};

const SKELETON_ROWS = [0, 1];

export default function PendingItemsSection(props: Props) {
  const hook = usePendingItemsSection(props.subTopicId);
  const totalCount = hook.items.length;

  return (
    <CardResource
      variant="pending_item"
      title="À suivre"
      subheader={
        totalCount === 0
          ? 'En attente d’un tiers ou d’un prestataire'
          : `En attente d’un tiers · ${hook.completedCount} sur ${totalCount} traité${hook.completedCount > 1 ? 's' : ''}`
      }
      action={hook.isAdmin ? (
        <Button
          variant="text"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={hook.handleOpenAddModal}
        >
          Ajouter
        </Button>
      ) : undefined}
    >
      {hook.isError ? (
        <Alert severity="error">Les éléments à suivre n&apos;ont pas pu être chargés. Rechargez la page pour réessayer.</Alert>
      ) : hook.isLoading ? (
        <Stack spacing={1} aria-busy="true" aria-label="Chargement des éléments à suivre">
          {SKELETON_ROWS.map((row) => (
            <Skeleton key={row} variant="text" height={32} />
          ))}
        </Stack>
      ) : totalCount === 0 ? (
        <Typography variant="noItem">
          Rien en attente d&apos;un tiers.{hook.isAdmin ? ' Ajoutez ce que vous attendez d’un prestataire : devis, passage, réponse.' : null}
        </Typography>
      ) : (
        <List dense disablePadding aria-label="Éléments à suivre">
          {hook.items.map((item) => (
            <TaskItem
              key={item.id}
              task={item}
              providerName={getPendingItemProviderLabel(item)}
              checked={item.state === 'completed'}
              onChange={(completed) => hook.toggleItem({ id: item.id, completed })}
              disabled={!hook.isAdmin}
              action={hook.isAdmin ? (
                <PendingItemMenuActions
                  item={item}
                  onEdit={() => hook.handleStartEdit(item)}
                  onDelete={() => hook.deleteItem(item.id)}
                />
              ) : undefined}
            />
          ))}
        </List>
      )}

      {hook.isAddModalOpen ? (
        <AddPendingItemModal
          open
          isAdding={hook.isAdding}
          onClose={hook.handleCloseAddModal}
          onAdd={hook.addItem}
        />
      ) : null}

      {hook.editingItem ? (
        <AddPendingItemModal
          open
          mode="edit"
          isAdding={hook.isUpdating}
          onClose={hook.handleCloseEdit}
          onAdd={hook.handleSaveEdit}
          initialTitle={hook.editingItem.title}
          initialProviderId={hook.editingItem.provider_id}
          initialProviderName={getPendingItemProviderLabel(hook.editingItem)}
        />
      ) : null}
    </CardResource>
  );
}
